import React, { useEffect, useState } from 'react'
import { collection, query, where, orderBy, onSnapshot, addDoc, doc, updateDoc } from 'firebase/firestore'
import { db } from '../../firebase.config'
import { useAuth } from '../../context/AuthContext'
import AppLayout from '../../components/layout/AppLayout'


const STATUS_COLORS: Record<string, string> = {
  assigned: 'bg-amber-100 text-amber-700',
  in_progress: 'bg-blue-100 text-blue-700',
  complete: 'bg-green-100 text-green-700',
  quality_verified: 'bg-emerald-100 text-emerald-700',
  failed_inspection: 'bg-red-100 text-red-700'
}

const STATUS_LABELS: Record<string, string> = {
  assigned: 'Assigned',
  in_progress: 'In Progress',
  complete: 'Complete',
  quality_verified: 'QC Verified',
  failed_inspection: 'Failed Inspection'
}

interface Task {
  id: string
  orgId: string
  projectId?: string
  pmProjectId?: string
  pmActivityId?: string
  title: string
  description?: string
  type: 'work' | 'rework'
  status: string
  assignedTo: string
  assignedToName?: string
  assignedAt?: string
  source?: 'pm' | 'admin'
  completedBy?: string
  completedByName?: string
  completedAt?: string
  completionNotes?: string
  completionPhotoUrls?: string[]
  sourceNcrId?: string
  ncrNumber?: string
  qualityResult?: string | null
  qualityVerifiedAt?: string
}

export default function TasksPage() {
  const { orgId, userData, isAdmin, isSupervisor } = useAuth()
  const [tasks, setTasks] = useState<Task[]>([])
  const [projects, setProjects] = useState<Record<string, string>>({})
  const [users, setUsers] = useState<{ id: string; name: string }[]>([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<Task | null>(null)
  const [showCreate, setShowCreate] = useState(false)

  // Filters
  const [statusFilter, setStatusFilter] = useState('all')
  const [typeFilter, setTypeFilter] = useState('all')
  const [search, setSearch] = useState('')

  // Create form
  const [form, setForm] = useState({
    title: '',
    description: '',
    assignedTo: '',
    projectId: '',
    type: 'work' as 'work' | 'rework'
  })
  const [creating, setCreating] = useState(false)

  const canCreate = isAdmin() || isSupervisor()

  useEffect(() => {
    if (!orgId) return

    let taskQuery
    if (canCreate) {
      taskQuery = query(
        collection(db, 'sitedeck_tasks'),
        where('orgId', '==', orgId),
        orderBy('assignedAt', 'desc')
      )
    } else {
      taskQuery = query(
        collection(db, 'sitedeck_tasks'),
        where('orgId', '==', orgId),
        where('assignedTo', '==', userData?.uid || ''),
        orderBy('assignedAt', 'desc')
      )
    }

    const unsubTasks = onSnapshot(taskQuery, snap => {
      setTasks(snap.docs.map(d => ({ id: d.id, ...d.data() } as Task)))
      setLoading(false)
    }, err => {
      console.error('Tasks snapshot error:', err)
      setLoading(false)
    })

    const unsubProjects = onSnapshot(
      query(collection(db, 'projects'), where('orgId', '==', orgId), orderBy('createdAt', 'desc')),
      snap => {
        const map: Record<string, string> = {}
        snap.docs.forEach(d => { map[d.id] = d.data().name || 'Unknown' })
        setProjects(map)
      }
    )

    const unsubUsers = onSnapshot(
      query(collection(db, 'organizations', orgId, 'users'), where('orgId', '==', orgId)),
      snap => {
        setUsers(snap.docs.map(d => ({ id: d.id, name: d.data().name || d.data().email || d.id })))
      }
    )

    return () => {
      unsubTasks()
      unsubProjects()
      unsubUsers()
    }
  }, [orgId, userData?.uid, canCreate])

  const filtered = tasks.filter(t => {
    if (statusFilter !== 'all' && t.status !== statusFilter) return false
    if (typeFilter !== 'all' && t.type !== typeFilter) return false
    const term = search.toLowerCase()
    return !term ||
      t.title.toLowerCase().includes(term) ||
      (t.assignedToName || '').toLowerCase().includes(term)
  })

  const formatDate = (iso?: string) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!orgId || !form.title.trim() || !form.assignedTo) return
    setCreating(true)
    try {
      const assignedUser = users.find(u => u.id === form.assignedTo)
      const createdAt = new Date().toISOString()
      await addDoc(collection(db, 'sitedeck_tasks'), {
        orgId,
        projectId: form.projectId || '',
        pmProjectId: '',
        pmActivityId: '',
        title: form.title.trim(),
        description: form.description.trim(),
        type: form.type,
        status: 'assigned',
        assignedTo: form.assignedTo,
        assignedToName: assignedUser?.name || '',
        assignedAt: createdAt,
        source: 'admin',
        completedBy: null,
        completedAt: null,
        completionNotes: '',
        completionPhotoUrls: [],
        sourceNcrId: null,
        ncrNumber: null,
        sourceTaskId: null,
        qualityResult: null,
        qualityVerifiedAt: null,
        createdAt,
        updatedAt: createdAt
      })
      setShowCreate(false)
      setForm({ title: '', description: '', assignedTo: '', projectId: '', type: 'work' })
    } catch (err: any) {
      console.error('Create task error:', err)
      alert('Failed to create task: ' + (err.message || 'permission denied'))
    } finally {
      setCreating(false)
    }
  }

  const handleVerifyQuality = async (result: 'passed' | 'failed') => {
    if (!selected) return
    try {
      await updateDoc(doc(db, 'sitedeck_tasks', selected.id), {
        qualityResult: result,
        qualityVerifiedAt: new Date().toISOString(),
        status: result === 'passed' ? 'quality_verified' : 'failed_inspection',
        updatedAt: new Date().toISOString()
      })
    } catch (err: any) {
      alert('Failed to update quality result: ' + err.message)
    }
  }

  return (
    <AppLayout title="Tasks">
      {/* Toolbar */}
      <div className="mb-6 flex flex-wrap items-center gap-4">
        {canCreate && (
          <button
            onClick={() => setShowCreate(true)}
            className="bg-accent text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-amber-700"
          >
            + Create Task
          </button>
        )}

        <input
          type="text"
          placeholder="Search by title or assignee..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="border border-gray-300 rounded-lg px-4 py-2 text-sm w-64"
        />

        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
        >
          <option value="all">All Statuses</option>
          <option value="assigned">Assigned</option>
          <option value="in_progress">In Progress</option>
          <option value="complete">Complete</option>
          <option value="quality_verified">QC Verified</option>
          <option value="failed_inspection">Failed Inspection</option>
        </select>

        <select
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
        >
          <option value="all">All Types</option>
          <option value="work">Work</option>
          <option value="rework">Rework</option>
        </select>
      </div>

      <div className={selected ? 'grid grid-cols-3 gap-6' : ''}>
        {/* Table */}
        <div className={selected ? 'col-span-2' : ''}>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-5 py-3 font-medium text-gray-600">Title</th>
                  <th className="text-left px-5 py-3 font-medium text-gray-600">Type</th>
                  <th className="text-left px-5 py-3 font-medium text-gray-600">Status</th>
                  <th className="text-left px-5 py-3 font-medium text-gray-600">Assigned To</th>
                  <th className="text-left px-5 py-3 font-medium text-gray-600">Project</th>
                  <th className="text-left px-5 py-3 font-medium text-gray-600">Source</th>
                  <th className="text-left px-5 py-3 font-medium text-gray-600">Assigned</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="px-5 py-12 text-center text-gray-400">Loading...</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={7} className="px-5 py-12 text-center text-gray-400">No tasks found</td></tr>
                ) : filtered.map(t => (
                  <tr
                    key={t.id}
                    className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${selected?.id === t.id ? 'bg-blue-50' : ''}`}
                    onClick={() => setSelected(t)}
                  >
                    <td className="px-5 py-3 font-medium text-gray-900">
                      <div className="flex items-center gap-2">
                        <div className="w-1 h-8 rounded-full" style={{ backgroundColor: t.type === 'rework' ? '#d64040' : '#1a2332' }} />
                        <span>{t.title}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className="px-2 py-0.5 rounded text-xs font-medium text-white"
                        style={{ backgroundColor: t.type === 'rework' ? '#d64040' : '#1a2332' }}
                      >
                        {t.type === 'rework' ? 'REWORK' : 'WORK'}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[t.status] || 'bg-gray-100 text-gray-700'}`}>
                        {STATUS_LABELS[t.status] || t.status}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-gray-600">{t.assignedToName || '—'}</td>
                    <td className="px-5 py-3 text-gray-600">{projects[t.projectId || ''] || t.projectId || '—'}</td>
                    <td className="px-5 py-3 text-gray-600 capitalize">{t.source || '—'}</td>
                    <td className="px-5 py-3 text-gray-600">{formatDate(t.assignedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Detail Panel */}
        {selected && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Task Detail</h3>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>

            <div className="space-y-4 text-sm">
              <div className="flex gap-2">
                <span
                  className="px-2 py-0.5 rounded text-xs font-medium text-white"
                  style={{ backgroundColor: selected.type === 'rework' ? '#d64040' : '#1a2332' }}
                >
                  {selected.type === 'rework' ? 'REWORK' : 'WORK'}
                </span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[selected.status] || 'bg-gray-100 text-gray-700'}`}>
                  {STATUS_LABELS[selected.status] || selected.status}
                </span>
              </div>

              <h4 className="font-semibold text-gray-900 text-base">{selected.title}</h4>
              <p className="text-gray-600">{selected.description || 'No description.'}</p>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-gray-500 uppercase">Project</p>
                  <p className="font-medium text-gray-800">{projects[selected.projectId || ''] || selected.projectId || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Source</p>
                  <p className="font-medium text-gray-800 capitalize">{selected.source || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Assigned To</p>
                  <p className="font-medium text-gray-800">{selected.assignedToName || selected.assignedTo}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Assigned Date</p>
                  <p className="font-medium text-gray-800">{formatDate(selected.assignedAt)}</p>
                </div>
              </div>

              {selected.type === 'rework' && selected.ncrNumber && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-xs text-red-600 uppercase font-semibold">NCR Number</p>
                  <p className="font-medium text-red-700">NCR-{selected.ncrNumber}</p>
                </div>
              )}

              {selected.status === 'complete' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-xs text-green-700 uppercase font-semibold mb-1">Completed</p>
                  <p className="text-sm text-gray-700">By {selected.completedByName || selected.completedBy} on {formatDate(selected.completedAt)}</p>
                  {selected.completionNotes && (
                    <p className="text-sm text-gray-700 mt-2">{selected.completionNotes}</p>
                  )}
                  {selected.completionPhotoUrls?.map((url, i) => (
                    <img key={i} src={url} alt="Completion" className="mt-3 rounded-lg max-h-40 object-cover" />
                  ))}
                </div>
              )}

              {selected.qualityResult && (
                <div className={`border rounded-lg p-4 ${selected.qualityResult === 'passed' ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                  <p className={`text-xs uppercase font-semibold ${selected.qualityResult === 'passed' ? 'text-emerald-700' : 'text-red-700'}`}>
                    Quality Result
                  </p>
                  <p className={`font-medium ${selected.qualityResult === 'passed' ? 'text-emerald-700' : 'text-red-700'}`}>
                    {selected.qualityResult.toUpperCase()} — {formatDate(selected.qualityVerifiedAt)}
                  </p>
                </div>
              )}

              {canCreate && selected.status === 'complete' && !selected.qualityResult && (
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500 uppercase mb-2">Record Quality Result</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleVerifyQuality('passed')}
                      className="flex-1 bg-emerald-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-emerald-700"
                    >
                      Pass
                    </button>
                    <button
                      onClick={() => handleVerifyQuality('failed')}
                      className="flex-1 bg-red-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-red-700"
                    >
                      Fail
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Create Task</h3>
              <button onClick={() => setShowCreate(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 uppercase mb-1">Title *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={e => setForm({ ...form, title: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 uppercase mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 uppercase mb-1">Assign To *</label>
                <select
                  value={form.assignedTo}
                  onChange={e => setForm({ ...form, assignedTo: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  required
                >
                  <option value="">Select user</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 uppercase mb-1">Project</label>
                <select
                  value={form.projectId}
                  onChange={e => setForm({ ...form, projectId: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">Select project</option>
                  {Object.entries(projects).map(([id, name]) => (
                    <option key={id} value={id}>{name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 uppercase mb-1">Type</label>
                <select
                  value={form.type}
                  onChange={e => setForm({ ...form, type: e.target.value as 'work' | 'rework' })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="work">Work</option>
                  <option value="rework">Rework</option>
                </select>
              </div>
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={creating}
                  className="w-full bg-accent text-white py-2.5 rounded-lg text-sm font-medium hover:bg-amber-700 disabled:opacity-50"
                >
                  {creating ? 'Creating...' : 'Create Task'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AppLayout>
  )
}
